import { Search } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useDebounce } from '../hooks/useDebounce';

interface SearchSelectProps<T> {
  placeholder?: string;
  onSearch: (query: string) => void;
  options: T[];
  renderOption: (option: T) => ReactNode;
  onSelect: (option: T) => void;
  selected?: T | null;
  loading?: boolean;
  renderSelected?: (option: T) => ReactNode;
}

export function SearchSelect<T>({
  placeholder = 'Search...',
  onSearch,
  options,
  renderOption,
  onSelect,
  selected,
  loading,
  renderSelected,
}: SearchSelectProps<T>) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    onSearch(debouncedQuery);
  }, [debouncedQuery, onSearch]);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (selected && renderSelected) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text">
          {renderSelected(selected)}
        </div>
        <button
          type="button"
          onClick={() => {
            onSelect(null as unknown as T);
            setQuery('');
          }}
          className="px-2 py-2 text-xs rounded-md border border-border text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
        >
          Clear
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          className="w-full pl-8 pr-3 py-2 bg-surface border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors"
        />
      </div>
      {open && (query || options.length > 0) && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-surface border border-border rounded-lg shadow-xl max-h-48 overflow-y-auto">
          {loading ? (
            <div className="px-3 py-4 text-sm text-text-muted text-center">Searching...</div>
          ) : options.length > 0 ? (
            options.map((option, i) => (
              <button
                key={i}
                onClick={() => {
                  onSelect(option);
                  setOpen(false);
                  setQuery('');
                }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-surface-hover transition-colors"
              >
                {renderOption(option)}
              </button>
            ))
          ) : query ? (
            <div className="px-3 py-4 text-sm text-text-muted text-center">No results</div>
          ) : null}
        </div>
      )}
    </div>
  );
}
